import styled from 'styled-components';
import isNil from 'lodash/isNil';
import HorizontalRuler from '../../../../shared/common/HorizontalRuler';
import { flex } from '../../../../styles/utilities/flex';
import { Checkbox } from '../../../../shared/common/Checkbox';
import { LCFormField } from '../../shared/LCFormField';
import { LCFormFieldValueText } from '../../shared/LCFormFieldValueText';
import { LCFormFieldValueList } from '../../shared/LCFormFieldValueList';
import { LibraryCreatedProductOut } from '../../../../store/apiLibx';
import { mapAuthors } from '../api/mappings/mapAuthors';
import { mapGenres } from '../api/mappings/mapGenres';
import { useTranslation } from 'react-i18next';

const StLCProductDetailMetaData = styled.div.attrs({ 'data-displayname': 'LCProductDetailMetaData' })`
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto;
    grid-column-gap: 12.5em;
`;

const StPairRow = styled.div`
    ${flex('row')};
    justify-content: flex-start;
    flex-grow: 1;
`;

const StRightCol = styled.div`
    grid-row-start: 1;
    grid-row-end: 2;
`;

const StLeftCol = styled.div`
    grid-row-start: 1;
    grid-row-end: 3;
`;

const StSpacerVertical = styled.span`
    display: block;
    padding-left: 2.5em;
    padding-right: 2.5em;
`;

const StSpacerHorizontal = styled.div`
    display: block;
    padding-bottom: 2.5em;
`;

const elementWrapperSmallWidth = '150px';
const elementWrapperMediumWidth = '300px';
const StElementWrapper = styled.div<{ width: string }>`
    width: ${props => props.width};
`;

const StAddFormField = styled(LCFormField)`
    min-height: unset;
`;

const spaceBetweenBlocksInPx = 130;

interface Props {
    product: LibraryCreatedProductOut;
}

export const LCProductDetailReadMetaData = (props: Props) => {
    const { t } = useTranslation();
    const isMarcRecordEditable = false;
    const isEditable = false;

    const marcCheckbox = (
        <Checkbox label={t('yourLocalContent.yourTitles.enableMarcRecords')} checked={false} disabled={!isEditable} />
    );

    const formattedDate = isNil(props.product.releaseDate)
        ? null
        : t('dateFormat.date', {
              val: new Date(props.product.releaseDate ?? 0)
          });
    return (
        <StLCProductDetailMetaData>
            <StRightCol>
                <StElementWrapper width={elementWrapperSmallWidth}>
                    <LCFormField
                        title={t('yourLocalContent.yourTitles.titlePrefix')}
                        isEdit={isEditable}
                        viewComponent={<LCFormFieldValueText text={props.product.titlePrefix} />}
                    />
                </StElementWrapper>
                <StSpacerHorizontal />
                <LCFormField
                    title={t('yourLocalContent.yourTitles.titleWithoutPrefix')}
                    isEdit={isEditable}
                    mandatory
                    viewComponent={<LCFormFieldValueText text={props.product.titleWithoutPrefix} bold />}
                />
                <StSpacerHorizontal />
                <LCFormField
                    title={t('yourLocalContent.yourTitles.subtitle')}
                    isEdit={isEditable}
                    viewComponent={<LCFormFieldValueText text={props.product.subTitle} />}
                />
                <StSpacerHorizontal />
                <StPairRow>
                    <LCFormField
                        title={t('yourLocalContent.yourTitles.seriesName')}
                        isEdit={isEditable}
                        viewComponent={<LCFormFieldValueText text={props.product.seriesName} />}
                    />

                    <StSpacerVertical />
                    <StElementWrapper width={elementWrapperSmallWidth}>
                        <LCFormField
                            title={t('yourLocalContent.yourTitles.seriesNumber')}
                            isEdit={isEditable}
                            viewComponent={<LCFormFieldValueText text={props.product.seriesNumber} />}
                        />
                    </StElementWrapper>
                </StPairRow>
                <HorizontalRuler heightSpacer={spaceBetweenBlocksInPx} />
                <StAddFormField
                    title={t('yourLocalContent.yourTitles.authors')}
                    isEdit={isEditable}
                    mandatory
                    viewComponent={
                        <LCFormFieldValueList value={mapAuthors(props.product.authors)} editable={isEditable} bold />
                    }
                />
                <HorizontalRuler heightSpacer={spaceBetweenBlocksInPx} />

                <StPairRow>
                    <StElementWrapper width={elementWrapperSmallWidth}>
                        <LCFormField
                            title={t('yourLocalContent.yourTitles.bsin')}
                            isEdit={isEditable}
                            viewComponent={<LCFormFieldValueText text={props.product.id} />}
                        />
                    </StElementWrapper>
                    <StSpacerVertical />
                    <StElementWrapper width={elementWrapperMediumWidth}>
                        <LCFormField
                            title={t('yourLocalContent.yourTitles.alternativeId')}
                            isEdit={isEditable}
                            viewComponent={<LCFormFieldValueText text={props.product.alternativeId} />}
                        />
                    </StElementWrapper>
                </StPairRow>
                <StSpacerHorizontal />
                <StPairRow>
                    <StElementWrapper width={elementWrapperSmallWidth}>
                        <LCFormField
                            title={t('yourLocalContent.yourTitles.format')}
                            isEdit={isEditable}
                            mandatory
                            viewComponent={
                                <LCFormFieldValueText
                                    text={t(
                                        `yourLocalContent.yourTitles.dropdowns.productFormat.${props.product.productFormat}`,
                                        {
                                            defaultValue: props.product.productFormat
                                        }
                                    )}
                                />
                            }
                        />
                    </StElementWrapper>
                    <StSpacerVertical />
                    <StElementWrapper width={elementWrapperMediumWidth}>
                        <LCFormField
                            title={t('yourLocalContent.yourTitles.numPages')}
                            isEdit={isEditable}
                            mandatory
                            viewComponent={<LCFormFieldValueText text={`${props.product.pages}`} />}
                        />
                    </StElementWrapper>
                </StPairRow>
                <StSpacerHorizontal />
                <StPairRow>
                    <StElementWrapper width={elementWrapperMediumWidth}>
                        <LCFormField
                            title={t('yourLocalContent.yourTitles.language')}
                            isEdit={isEditable}
                            mandatory
                            viewComponent={<LCFormFieldValueText text={props.product.language} />}
                        />
                    </StElementWrapper>
                </StPairRow>
                <HorizontalRuler heightSpacer={spaceBetweenBlocksInPx} />

                <LCFormField
                    title={t('yourLocalContent.yourTitles.providedBy')}
                    isEdit={isEditable}
                    viewComponent={<LCFormFieldValueText text={props.product.publisher} />}
                />
                <StSpacerHorizontal />
                <LCFormField
                    title={t('yourLocalContent.yourTitles.publishedBy')}
                    isEdit={isEditable}
                    viewComponent={<LCFormFieldValueText text={props.product.publisherImprint} />}
                />
                <StSpacerHorizontal />
                <StElementWrapper width={elementWrapperMediumWidth}>
                    <LCFormField
                        title={t('yourLocalContent.yourTitles.releaseDate')}
                        isEdit={isEditable}
                        viewComponent={<LCFormFieldValueText text={formattedDate ?? ''} />}
                    />
                </StElementWrapper>
            </StRightCol>
            <StLeftCol>
                <LCFormField
                    title={t('yourLocalContent.yourTitles.teaser')}
                    isEdit={isEditable}
                    viewComponent={<LCFormFieldValueText text={props.product.teaser} />}
                />

                <StSpacerHorizontal />
                <LCFormField
                    title={t('yourLocalContent.yourTitles.description')}
                    isEdit={isEditable}
                    viewComponent={<LCFormFieldValueText text={props.product.description} multiLine />}
                />
                <HorizontalRuler heightSpacer={spaceBetweenBlocksInPx} />

                <StPairRow>
                    <StElementWrapper width={elementWrapperSmallWidth}>
                        <LCFormField
                            title={t('yourLocalContent.yourTitles.ageCategory')}
                            isEdit={isEditable}
                            viewComponent={
                                <LCFormFieldValueText
                                    text={t(`loanFormat.ageCategory.${props.product.ageCategory}`, {
                                        defaultValue: props.product.ageCategory
                                    })}
                                />
                            }
                        />
                    </StElementWrapper>
                    <StSpacerVertical />
                    <StElementWrapper width={elementWrapperSmallWidth}>
                        <LCFormField
                            title={t('yourLocalContent.yourTitles.ageGroup')}
                            isEdit={isEditable}
                            viewComponent={
                                <LCFormFieldValueText
                                    text={t(
                                        `yourLocalContent.yourTitles.dropdowns.ageGroup.${props.product.ageGroup}`,
                                        {
                                            defaultValue: t('yourLocalContent.yourTitles.all')
                                        }
                                    )}
                                />
                            }
                        />
                    </StElementWrapper>
                </StPairRow>
                <StSpacerHorizontal />

                <StPairRow>
                    <StElementWrapper width={elementWrapperSmallWidth}>
                        <LCFormField
                            title={t('yourLocalContent.yourTitles.fiction')}
                            isEdit={isEditable}
                            viewComponent={
                                <LCFormFieldValueText
                                    text={t(`yourLocalContent.yourTitles.fictionDropdown.${props.product.fiction}`, {
                                        defaultValue: t('yourLocalContent.yourTitles.all')
                                    })}
                                />
                            }
                        />
                    </StElementWrapper>
                    <StSpacerVertical />
                    <StElementWrapper width={elementWrapperSmallWidth}>
                        <LCFormField
                            title={t('yourLocalContent.yourTitles.explicit')}
                            isEdit={isEditable}
                            viewComponent={
                                <LCFormFieldValueText
                                    text={
                                        props.product.explicit
                                            ? t('yourLocalContent.yourTitles.yes')
                                            : t('yourLocalContent.yourTitles.no')
                                    }
                                />
                            }
                        />
                    </StElementWrapper>
                </StPairRow>
                <HorizontalRuler heightSpacer={spaceBetweenBlocksInPx} />
                <LCFormField
                    title={t('yourLocalContent.yourTitles.localContentCategory')}
                    isEdit={isEditable}
                    viewComponent={
                        <LCFormFieldValueText
                            text={t(`filters:genre.${props.product.genreCategory?.id}`, {
                                defaultValue: props.product.genreCategory?.name
                            })}
                        />
                    }
                />
                <StSpacerHorizontal />

                <StAddFormField
                    title={t('yourLocalContent.yourTitles.localGenre')}
                    isEdit={isEditable}
                    viewComponent={
                        <LCFormFieldValueList value={mapGenres(props.product, t).localGenres} editable={isEditable} />
                    }
                />
                <StSpacerHorizontal />
                <StAddFormField
                    title={t('yourLocalContent.yourTitles.standardBorrowBoxGenres')}
                    isEdit={isEditable}
                    viewComponent={
                        <LCFormFieldValueList
                            value={mapGenres(props.product, t).standardGenres}
                            editable={isEditable}
                        />
                    }
                />
                <HorizontalRuler heightSpacer={spaceBetweenBlocksInPx} />

                <StAddFormField
                    title={t('yourLocalContent.yourTitles.keywords')}
                    isEdit={isEditable}
                    viewComponent={<LCFormFieldValueList value={props.product.keywords ?? []} />}
                />
                <HorizontalRuler heightSpacer={spaceBetweenBlocksInPx} />
                <LCFormField title="&#160;" isEdit={isEditable} viewComponent={marcCheckbox} />
                <StSpacerHorizontal />
                <LCFormField
                    title={t('yourLocalContent.yourTitles.marcTitle')}
                    isEdit={isMarcRecordEditable}
                    viewComponent={<LCFormFieldValueText text={props.product.marcTitleWithoutPrefix} />}
                />

                <StSpacerHorizontal />
                <LCFormField
                    title={t('yourLocalContent.yourTitles.marcSubjectHeadings')}
                    isEdit={isMarcRecordEditable}
                    viewComponent={<LCFormFieldValueText text={props.product.marcSubjectHeadings} multiLine />}
                />
                <StSpacerHorizontal />
                <LCFormField
                    title={t('yourLocalContent.yourTitles.marcGenreHeadings')}
                    isEdit={isMarcRecordEditable}
                    viewComponent={<LCFormFieldValueText text={props.product.marcGenreHeadings} />}
                />
                <StSpacerHorizontal />
                <LCFormField
                    title={t('yourLocalContent.yourTitles.marcGeographicalHeadings')}
                    isEdit={isMarcRecordEditable}
                    viewComponent={<LCFormFieldValueText text={props.product.marcGeographicSubjects} multiLine />}
                />
                <StSpacerHorizontal />
                <LCFormField
                    title={t('yourLocalContent.yourTitles.marcBiographicalSubjectAuthorisedName')}
                    isEdit={isMarcRecordEditable}
                    viewComponent={<LCFormFieldValueText text={props.product.marcBiographicSubjects} multiLine />}
                />
                <StSpacerHorizontal />
                <LCFormField
                    title={t('yourLocalContent.yourTitles.marcSeriesAuthorisedName')}
                    isEdit={isMarcRecordEditable}
                    viewComponent={<LCFormFieldValueText text={props.product.marcSeries} multiLine />}
                />
            </StLeftCol>
        </StLCProductDetailMetaData>
    );
};
